import { useMutation } from '@apollo/client';
import type { InsertQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertQuestionnaireTemplateVersionDocument,
  namedOperations,
  Questionnaire_Template_Version_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type InsertQuestionnaireTemplateVersionInput,
  useInsertQuestionnaireTemplateVersionTRPC,
} from './useInsertQuestionnaireTemplateVersionTRPC';

/**
 * Flag-gated wrapper for the `insertQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 *
 * `questionnaire_template_version` is node-backed; the tRPC path
 * writes asynchronously via `executeAsyncRequest`/request-state
 * tracking, but is not a governed entity, so it does not go
 * through the approval/change-request workflow.
 */
export const useInsertQuestionnaireTemplateVersion = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertQuestionnaireTemplateVersionDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template_version');
        evictField(cache, 'questionnaire_template');
        evictField(cache, 'questionnaire_template_by_pk');
      },
      refetchQueries: [
        namedOperations.Query.getQuestionnaireTemplateById,
        namedOperations.Query.getQuestionnaireTemplates,
      ],
    }
  );

  const trpcMutation = useInsertQuestionnaireTemplateVersionTRPC();

  const insertQuestionnaireTemplateVersion = async (
    input: InsertQuestionnaireTemplateVersionInput
  ): Promise<InsertQuestionnaireTemplateVersionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertQuestionnaireTemplateVersion(input);
    }

    const result = await insertGraphQL({
      variables: {
        object: {
          Version: input.Version,
          Schema: input.Schema,
          UISchema: input.UISchema,
          ParentId: input.ParentId,
          // NB: `questionnaire_template_version_insert_input` has no
          // CustomAttributeData field — the legacy insert never persisted it.
          Status: Questionnaire_Template_Version_Status_Enum.Draft,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert questionnaire template version');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertQuestionnaireTemplateVersion,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertQuestionnaireTemplateVersion,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
